package com.mgacreative.hotel.feature.admin.ui

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import com.mgacreative.hotel.core.network.SupabaseNetworkClient
import com.mgacreative.hotel.core.tenant.TenantSession
import io.github.jan.supabase.postgrest.postgrest
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.launch
import kotlinx.serialization.Serializable
import kotlinx.serialization.json.JsonArray
import kotlinx.serialization.json.JsonObject
import kotlinx.serialization.json.JsonPrimitive
import kotlinx.serialization.json.booleanOrNull
import kotlinx.serialization.json.buildJsonArray
import kotlinx.serialization.json.buildJsonObject
import kotlinx.serialization.json.jsonPrimitive
import kotlinx.serialization.json.put

@Serializable
data class Member(
    val id: String = "",
    val email: String = "",
    val full_name: String? = "",
    val member_code: String? = "",
    val phone: String? = null,
    val is_active: Boolean = true,
    val start_date: String? = null,
    val end_date: String? = null,
    val hotel_name: String? = null,
    val tenant_id: String? = null,
    val created_at: String? = null,
    val allowed_modules: List<String> = listOf("FRONT_DESK", "ROOM_STATUS", "STAFF", "SYSTEM")
)

@Serializable
private data class TenantInfo(
    val id: String,
    val name: String? = null,
    val phone: String? = null,
    val company_title: String? = null
)

sealed interface MemberListUiState {
    data object Loading : MemberListUiState
    data class Success(
        val members: List<Member>,
        val searchQuery: String = "",
        val selectedStatus: Boolean? = null // null: Tümü, true: Aktif, false: Pasif/Kısıtlı
    ) : MemberListUiState {
        val filteredMembers: List<Member>
            get() = members.filter { member ->
                val query = searchQuery.trim()
                val matchesQuery = query.isBlank() ||
                        (member.full_name?.contains(query, ignoreCase = true) == true) ||
                        member.email.contains(query, ignoreCase = true) ||
                        (member.member_code?.contains(query, ignoreCase = true) == true) ||
                        (member.hotel_name?.contains(query, ignoreCase = true) == true) ||
                        (member.phone?.contains(query, ignoreCase = true) == true)
                val matchesStatus = selectedStatus == null || member.is_active == selectedStatus
                matchesQuery && matchesStatus
            }

        val totalCount: Int get() = members.size
        val activeCount: Int get() = members.count { it.is_active }
        val passiveCount: Int get() = members.count { !it.is_active }
    }
    data class Error(val message: String) : MemberListUiState
}

class AdminMembersViewModel(
    private val supabaseClient: SupabaseNetworkClient,
    private val tenantSession: TenantSession
) : ViewModel() {

    private val _uiState = MutableStateFlow<MemberListUiState>(MemberListUiState.Loading)
    val uiState: StateFlow<MemberListUiState> = _uiState.asStateFlow()

    private val _actionMessage = MutableStateFlow<String?>(null)
    val actionMessage: StateFlow<String?> = _actionMessage.asStateFlow()

    private val _isSaving = MutableStateFlow(false)
    val isSaving: StateFlow<Boolean> = _isSaving.asStateFlow()

    init {
        loadMembers()
    }

    fun loadMembers() {
        viewModelScope.launch {
            _uiState.value = MemberListUiState.Loading
            try {
                val currentSearch = (_uiState.value as? MemberListUiState.Success)?.searchQuery ?: ""
                val currentFilter = (_uiState.value as? MemberListUiState.Success)?.selectedStatus

                // 1. Tüm Otel/Tenant Profillerini Çek (Gerçek Otel Adı ve Telefon Kaynağı)
                val tenantsList = try {
                    supabaseClient.client.postgrest["tenants"]
                        .select()
                        .decodeList<TenantInfo>()
                } catch (e: Exception) {
                    emptyList()
                }
                val tenantsMap = tenantsList.associateBy { it.id }

                // 2. Kullanıcıları users tablosundan dinamik ve güvenli çek
                val usersJson = try {
                    supabaseClient.client.postgrest["users"]
                        .select()
                        .decodeList<JsonObject>()
                } catch (e: Exception) {
                    emptyList()
                }

                val membersList = usersJson.map { obj ->
                    val id = obj["id"]?.jsonPrimitive?.content ?: ""
                    val email = obj["email"]?.jsonPrimitive?.content ?: ""
                    val fullName = obj["full_name"]?.jsonPrimitive?.content 
                        ?: obj["email"]?.jsonPrimitive?.content?.substringBefore("@") 
                        ?: "Kullanıcı"
                    val memberCode = obj["member_code"]?.jsonPrimitive?.content
                        ?: ("MBR-" + if (id.length >= 6) id.take(6).uppercase() else "ADMIN")
                    val isActive = obj["is_active"]?.jsonPrimitive?.booleanOrNull ?: true
                    val startDate = obj["start_date"]?.jsonPrimitive?.content
                    val endDate = obj["end_date"]?.jsonPrimitive?.content
                    val tenantId = obj["tenant_id"]?.jsonPrimitive?.content
                    val createdAt = obj["created_at"]?.jsonPrimitive?.content

                    // Kullanıcının kendi bireysel otel adı ve telefon bilgisi
                    val userHotelName = obj["hotel_name"]?.jsonPrimitive?.content?.takeIf { it.isNotBlank() }
                    val userPhone = obj["phone"]?.jsonPrimitive?.content?.takeIf { it.isNotBlank() }

                    val tenant = tenantId?.let { tenantsMap[it] }
                    val realHotelName = userHotelName 
                        ?: tenant?.name?.takeIf { it.isNotBlank() } 
                        ?: tenant?.company_title?.takeIf { it.isNotBlank() }
                        ?: ""
                    val realPhone = userPhone 
                        ?: tenant?.phone?.takeIf { it.isNotBlank() }
                        ?: ""

                    val allowedModules = obj["allowed_modules"]?.let { element ->
                        try {
                            if (element is JsonArray) {
                                element.mapNotNull { it.jsonPrimitive.content }
                            } else null
                        } catch (_: Exception) { null }
                    }?.takeIf { it.isNotEmpty() } ?: listOf("FRONT_DESK", "ROOM_STATUS", "STAFF", "SYSTEM")

                    Member(
                        id = id,
                        email = email,
                        full_name = fullName,
                        member_code = memberCode,
                        phone = realPhone,
                        is_active = isActive,
                        start_date = startDate,
                        end_date = endDate,
                        hotel_name = realHotelName,
                        tenant_id = tenantId,
                        created_at = createdAt,
                        allowed_modules = allowedModules
                    )
                }

                _uiState.value = MemberListUiState.Success(
                    members = membersList,
                    searchQuery = currentSearch,
                    selectedStatus = currentFilter
                )
            } catch (e: Exception) {
                _uiState.value = MemberListUiState.Error(e.message ?: "Üyeler yüklenemedi.")
            }
        }
    }

    fun onSearchQueryChange(query: String) {
        val currentState = _uiState.value as? MemberListUiState.Success ?: return
        _uiState.value = currentState.copy(searchQuery = query)
    }

    fun onFilterStatusChange(status: Boolean?) {
        val currentState = _uiState.value as? MemberListUiState.Success ?: return
        _uiState.value = currentState.copy(selectedStatus = status)
    }

    fun updateMember(member: Member) {
        viewModelScope.launch {
            _isSaving.value = true
            try {
                // Optimistic UI update
                val currentState = _uiState.value as? MemberListUiState.Success
                if (currentState != null) {
                    val updatedList = currentState.members.map {
                        if (it.id == member.id) member else it
                    }
                    _uiState.value = currentState.copy(members = updatedList)
                }

                // 1. users tablosunu güncelle
                supabaseClient.client.postgrest["users"]
                    .update(
                        buildJsonObject {
                            put("member_code", member.member_code?.trim()?.uppercase() ?: "")
                            put("is_active", member.is_active)
                            if (!member.start_date.isNullOrBlank()) put("start_date", member.start_date)
                            if (!member.end_date.isNullOrBlank()) put("end_date", member.end_date)
                            put("hotel_name", member.hotel_name ?: "")
                            put("phone", member.phone ?: "")
                            put("allowed_modules", buildJsonArray {
                                member.allowed_modules.forEach { add(JsonPrimitive(it)) }
                            })
                        }
                    ) {
                        filter { eq("id", member.id) }
                    }

                // 2. tenants tablosundaki otel profilini güncelle
                if (!member.tenant_id.isNullOrBlank()) {
                    try {
                        supabaseClient.client.postgrest["tenants"]
                            .update(
                                buildJsonObject {
                                    put("name", member.hotel_name ?: "")
                                    put("phone", member.phone ?: "")
                                }
                            ) {
                                filter { eq("id", member.tenant_id) }
                            }
                    } catch (eTenant: Exception) {
                        eTenant.printStackTrace()
                    }
                }

                // 3. members tablosunu senkronize et
                try {
                    val updatePayload = buildJsonObject {
                        put("id", member.id)
                        put("email", member.email)
                        put("full_name", member.full_name ?: "")
                        put("member_code", member.member_code?.trim()?.uppercase() ?: "")
                        if (!member.start_date.isNullOrBlank()) put("start_date", member.start_date)
                        if (!member.end_date.isNullOrBlank()) put("end_date", member.end_date)
                        put("hotel_name", member.hotel_name ?: "")
                        put("phone", member.phone ?: "")
                        put("is_active", member.is_active)
                    }
                    supabaseClient.client.postgrest["members"].upsert(updatePayload)
                } catch (eMem: Exception) {
                    // Ignore if members table not used
                }

                _actionMessage.value = "Üye ve Otel bilgileri başarıyla kaydedildi."
            } catch (e: Exception) {
                _actionMessage.value = "Güncelleme hatası: ${e.message}"
                loadMembers()
            } finally {
                _isSaving.value = false
            }
        }
    }

    fun updateMemberAllowedModules(memberId: String, newModules: List<String>) {
        val currentState = _uiState.value as? MemberListUiState.Success ?: return
        val member = currentState.members.find { it.id == memberId } ?: return
        val updated = member.copy(allowed_modules = newModules)
        updateMember(updated)
    }

    fun toggleMemberStatus(member: Member) {
        val newStatus = !member.is_active
        viewModelScope.launch {
            _isSaving.value = true
            try {
                // Optimistic UI update
                val currentState = _uiState.value as? MemberListUiState.Success
                if (currentState != null) {
                    val updatedList = currentState.members.map {
                        if (it.id == member.id) it.copy(is_active = newStatus) else it
                    }
                    _uiState.value = currentState.copy(members = updatedList)
                }

                // 1. users tablosunda is_active durumunu güncelle
                supabaseClient.client.postgrest["users"]
                    .update(
                        buildJsonObject {
                            put("is_active", newStatus)
                        }
                    ) {
                        filter { eq("id", member.id) }
                    }

                // 2. members tablosunda is_active durumunu güncelle
                try {
                    supabaseClient.client.postgrest["members"]
                        .update(
                            buildJsonObject {
                                put("is_active", newStatus)
                            }
                        ) {
                            filter { eq("id", member.id) }
                        }
                } catch (_: Exception) {}

                val memberName = member.full_name?.takeIf { it.isNotBlank() } ?: member.email
                _actionMessage.value = if (newStatus) {
                    "$memberName hesabı AKTİF yapıldı."
                } else {
                    "$memberName hesabı PASİFE alındı."
                }
            } catch (e: Exception) {
                _actionMessage.value = "Durum değiştirilemedi: ${e.message}"
                loadMembers()
            } finally {
                _isSaving.value = false
            }
        }
    }

    fun clearActionMessage() {
        _actionMessage.value = null
    }
}
